// Externals imports.
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import React, { ReactElement, useContext, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core'
import { LanguageMyReportList, LanguagePrompt } from '../../../../i18n'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Checkbox from '../../Common/Components/Checkbox'
import ContextMenu, { ContextMenuPosition } from '../../Common/Components/ContextMenu'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { MessageContext, PromptContext, ServletPathContext } from '../../Common/Contexts'
import { ReactState, StateSetter } from '../../Common/Types/Types'
import {
    matchParentFolderId,
    PrepareDeletePermanentlyResultMessage,
    PrepareRestoreResultMessage,
    recursiveSubFolderGet
} from '../../Common/Utils/ReportList'
import { deleteReport, deleteReportFromBin, restoreReportFromBin } from './API'
import PromptMessageWithWarning from './PromptMessageWithWarning'
import { ReportListItemsIndexByReportID, ReportListItemType } from './Types'

/**
 * Component properties type.
 */
type Props = {
    reportListItem: ReportListItemType
    isParentFolderHighlighted: boolean
    propagatedValues: BinnedReportListItemPropagatedValues
}

export type BinnedReportListItemPropagatedValues = {
    isReportWriteAllowed: boolean
    hoveredItemID: Nullable<string>
    setHoveredItemID: StateSetter<Nullable<string>>
    checkedItemsIDs: string[]
    setCheckedItemsIDs: StateSetter<string[]>
    reportListItemsAsMap: ReportListItemsIndexByReportID
    setReportListItemsAsMap: StateSetter<ReportListItemsIndexByReportID>
    initialiseReportListData: () => Promise<void>
    setHasHighlight: (value: boolean) => void
}

/**
 * The binned report list item that could be the folder or a report.
 * The structure is build as a tree based on the report list API.
 *
 * @param reportListItem The report list item - report or folder.
 * @param isReportWriteAllowed Whether the user has write permissions for report entity.
 * @param hoveredItemID The ID of currently hovered item.
 * @param setHoveredItemID Setter for corresponding property.
 * @param checkedItemsIDs Array of checked items IDs.
 * @param setCheckedItemsIDs The setter for corresponding array.
 * @param reportListItemsAsMap Report items as map indexed by ID.
 * @param setReportListItemsAsMap The setter for corresponding report items map.
 * @param initialiseReportListData Report list data initialisation method.
 * @param setHasHighlight Set is there a highlighted row.
 * @param isParentFolderHighlighted Set is there a highlighted row.
 */
const BinnedReportListItem: React.FC<Props> = ({
    reportListItem,
    propagatedValues,
    isParentFolderHighlighted
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('LanguageMyReportList')
    const servletPath = useContext(ServletPathContext) as string
    const { displayPrompt } = useContext(PromptContext)
    const { displayError, displayErrorMessage, displaySuccessMessage, displayWarningMessage } =
        useContext(MessageContext)

    const {
        isReportWriteAllowed,
        hoveredItemID,
        setHoveredItemID,
        checkedItemsIDs,
        setCheckedItemsIDs,
        reportListItemsAsMap,
        setReportListItemsAsMap,
        initialiseReportListData,
        setHasHighlight
    } = propagatedValues

    const { id, name, subItems, isScheduleActive, durationFilterSummary } = reportListItem
    const isHovered = id === hoveredItemID
    const isChecked = checkedItemsIDs.includes(id)

    // These are the values we will propagate down to the report list items in case of deeper items hierarchy.

    const [isHighlighted, setIsHighlighted]: ReactState<boolean> = useState<boolean>(false)
    const [isFolderHighlighted, setIsFolderHighlighted]: ReactState<boolean> = useState<boolean>(false)

    const [isShowContentUntilTransitionFinishes, setIsShowContentUntilTransitionFinishes] = useState<boolean>(false)

    /**
     * Unchecked folder sub items.
     *
     * @param folderID - ID of the folder to uncheck sub items for.
     * @param reportIDsToUncheck - List of report IDs to uncheck if they are in folders.
     */
    const uncheckFolderSubItems = (folderID: string, reportIDsToUncheck?: string[]): void => {
        // Get a list of all of the report list items.
        const reportList = Array.from(reportListItemsAsMap.values())
        // List of folder IDs.
        const folders: string[] = recursiveSubFolderGet(reportList, [folderID])

        if (reportIDsToUncheck) {
            // Uncheck only provided list of reportIDs within the folders.
            const reportItemIDsToUncheck = reportList
                // Find the items in this folder
                .filter(
                    (currentReportListItem) =>
                        matchParentFolderId(currentReportListItem, folders) &&
                        currentReportListItem.isReport &&
                        reportIDsToUncheck.includes(currentReportListItem.id)
                )
                .map((currentReportListItem): string => currentReportListItem.id)

            setCheckedItemsIDs((prevCheckedItemsIDs: string[]): string[] =>
                prevCheckedItemsIDs.filter(
                    (prevCheckedItemID): boolean => !reportItemIDsToUncheck.includes(prevCheckedItemID)
                )
            )
        } else {
            // Uncheck all selected options within a folder.
            reportList
                // Find the items in this folder
                .filter(
                    (currentReportListItem) =>
                        matchParentFolderId(currentReportListItem, folders) && currentReportListItem.isReport
                )
                .forEach((currentReportListItem) => {
                    // Uncheck every item
                    setCheckedItemsIDs((prevCheckedItemsIDs): string[] =>
                        ~checkedItemsIDs.includes(currentReportListItem.id)
                            ? prevCheckedItemsIDs.filter((itemID) => itemID !== currentReportListItem.id)
                            : prevCheckedItemsIDs
                    )
                })
        }
    }

    /**
     * Folder item click handler.
     * @param event click event.
     * @param rptListItemID The identifier of the menu item.
     */
    const handleReportListFolderClick = (
        event: React.MouseEvent | React.ChangeEvent<unknown> | null,
        rptListItemID: string
    ): void => {
        event?.stopPropagation()

        const folder: ReportListItemType | undefined = reportListItemsAsMap.get(rptListItemID)

        if (folder) {
            setReportListItemsAsMap((pPrevStateReportsAsMap) => {
                setCheckedItemsIDs((prevCheckedItemsIDs): string[] => {
                    if (checkedItemsIDs.includes(folder.id)) {
                        // Folder was already expanded and we have to collapse it.
                        return prevCheckedItemsIDs.filter((itemID) => itemID !== folder.id)
                    }
                    // Folder was collapsed and we have to expand it.
                    return [...prevCheckedItemsIDs, folder.id]
                })

                return new Map(pPrevStateReportsAsMap)
            })

            // Uncheck all sub items.
            uncheckFolderSubItems(rptListItemID)
        }
    }

    /**
     * Callback when mouse entered the report list item (report or folder) element.
     * @param rptListItemID
     */
    const handleMouseEnteredReportListItem = (rptListItemID: string): void => {
        setHoveredItemID(rptListItemID)
    }

    /**
     * Callback when mouse leaves the report list item (report or folder) element.
     * @param rptListItemID
     */
    const handleMouseLeftReportListItem = (rptListItemID: string): void => {
        setHoveredItemID((previousID): Nullable<string> => (previousID === rptListItemID ? null : previousID))
    }

    /**
     * Delete report click handler.
     * @param reportID ID of the report to delete.
     */
    const handleReportDelete = async (reportID: string): Promise<void> => {
        let isError = false
        try {
            // eslint-disable-next-line
            await deleteReportFromBin(servletPath, reportID)
        } catch (error) {
            displayError(error as Error)
            isError = true
        }
        initialiseReportListData()

        if (!isError) {
            displaySuccessMessage(language.get(LanguageMyReportList.ReportlistReportwasdeletedpermanently))

            setCheckedItemsIDs((prevCheckedItemsIDs) =>
                prevCheckedItemsIDs.filter((rptListItemID) => rptListItemID !== reportID)
            )
        }
    }

    /**
     * Restore report click handler.
     * @param reportID ID of the report to restore.
     */
    const handleReportRestore = async (reportID: string): Promise<void> => {
        let isError = false
        try {
            await restoreReportFromBin(servletPath, reportID)
        } catch (error) {
            displayError(error as Error)
            isError = true
        } finally {
            initialiseReportListData()
        }

        if (!isError) {
            displaySuccessMessage(language.get(LanguageMyReportList.ReportlistReportwasrestored))

            setCheckedItemsIDs((prevCheckedItemsIDs) =>
                prevCheckedItemsIDs.filter((rptListItemID) => rptListItemID !== reportID)
            )
        }
    }

    /**
     * Prompt popup warning before deletion of reports.
     */
    const displayPromptOnDelete = (reportID: string): void => {
        displayPrompt(
            <PromptMessageWithWarning
                message={language?.get(LanguagePrompt.PromptDeletereport)}
                warningMessage={language?.get(LanguagePrompt.PromptReportswillbedeletedpermanently)}
            />,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            (): void => {
                handleReportDelete(reportID)
            },
            undefined,
            undefined,
            undefined
        )
    }

    const onMouseOver = (): void => {
        setIsHighlighted(true)
        setHasHighlight(true)
    }
    const onMouseOut = (): void => {
        setIsHighlighted(false)
        setHasHighlight(false)
    }

    const onFolderActionsMouseOver = (): void => {
        setIsFolderHighlighted(true)
        setHasHighlight(true)
    }
    const onFolderActionsMouseOut = (): void => {
        setIsFolderHighlighted(false)
        setHasHighlight(false)
    }

    const handleReportListCheckboxClick = (checked: boolean, rptListItemID: string): void => {
        setReportListItemsAsMap((prevStateReportsAsMap): Map<string, ReportListItemType> => {
            const reportItem = prevStateReportsAsMap.get(rptListItemID)
            if (reportItem) {
                setCheckedItemsIDs((prevCheckedItemsIDs): string[] =>
                    checkedItemsIDs.includes(reportItem.id)
                        ? // Report was already selected and we have to unselect it.
                          prevCheckedItemsIDs.filter((itemID) => itemID !== reportItem.id)
                        : // Report was not selected yet and we have to select it.
                          [...prevCheckedItemsIDs, reportItem.id]
                )
            }
            return new Map(prevStateReportsAsMap)
        })
    }

    const findAllReportsInFolderRecursively = (folderItem: ReportListItemType): string[] => {
        let reportIDsToDelete: string[] = []
        if (folderItem.subItems?.length) {
            folderItem.subItems?.forEach((item): void => {
                if (item.isReport) {
                    reportIDsToDelete.push(item.id)
                } else {
                    reportIDsToDelete = [...reportIDsToDelete, ...findAllReportsInFolderRecursively(item)]
                }
            })
        }
        return reportIDsToDelete
    }

    const handleDeleteItemsInFolder = async (folderItem: ReportListItemType): Promise<void> => {
        const reportIDsToDelete = findAllReportsInFolderRecursively(folderItem)
        const deletedReportIDs: string[] = []
        const failedReportIDs: string[] = []

        // Try to delete the reports.
        for (let index = 0; index < reportIDsToDelete.length; index += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await deleteReport(servletPath, reportIDsToDelete[index])
                // Add the ID of deleted item to the temporary array so that we can then "unselect" all
                // successfully deleted items.
                deletedReportIDs.push(reportIDsToDelete[index])
            } catch (error) {
                failedReportIDs.push(reportIDsToDelete[index])
            }
        }

        // Display toast message to the user.
        if (deletedReportIDs.length === 0) {
            // Error
            displayErrorMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIDs.length, reportIDsToDelete.length)
            )
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIDs.length, reportIDsToDelete.length)
            )
        } else if (failedReportIDs.length > 0 && deletedReportIDs.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIDs.length, reportIDsToDelete.length)
            )
        }

        // Uncheck items that were deleted.
        uncheckFolderSubItems(folderItem.id, deletedReportIDs)

        // Reload report list.
        initialiseReportListData()
    }

    const handleRestoreItemsInFolder = async (folderItem: ReportListItemType): Promise<void> => {
        const reportIDsToRestore = findAllReportsInFolderRecursively(folderItem)
        const restoredReportIDs: string[] = []
        const failedReportIDs: string[] = []

        // Try to delete the reports.
        for (let index = 0; index < reportIDsToRestore.length; index += 1) {
            try {
                // eslint-disable-next-line
                const reportDeleteResponse = await restoreReportFromBin(servletPath, reportIDsToRestore[index])
                // Add the ID of deleted item to the temporary array so that we can then "unselect" all
                // successfully restored items.
                restoredReportIDs.push(reportIDsToRestore[index])
            } catch (error) {
                failedReportIDs.push(reportIDsToRestore[index])
            }
        }

        // Display toast message to the user.
        if (restoredReportIDs.length === 0) {
            // Error
            displayErrorMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, reportIDsToRestore.length)
            )
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, reportIDsToRestore.length)
            )
        } else if (failedReportIDs.length > 0 && restoredReportIDs.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, reportIDsToRestore.length)
            )
        }

        // Uncheck items that were restored.
        uncheckFolderSubItems(folderItem.id, restoredReportIDs)

        // Reload report list.
        initialiseReportListData()
    }

    return !!subItems && subItems.length > 0 ? (
        <div
            className={`report-list-wrapper ${
                isFolderHighlighted || isParentFolderHighlighted ? 'highlighted-item' : ''
            }`}
        >
            <Accordion
                key={`${name}-${id}`}
                expanded={isChecked}
                onChange={(event: React.MouseEvent<Element, MouseEvent> | React.ChangeEvent<unknown> | null): void =>
                    handleReportListFolderClick(event, id)
                }
                TransitionProps={{
                    onEnter: (): void => setIsShowContentUntilTransitionFinishes(true),
                    onExited: (): void => setIsShowContentUntilTransitionFinishes(false)
                }}
            >
                <AccordionSummary
                    className={`summary ${isFolderHighlighted || isParentFolderHighlighted ? 'highlighted-item' : ''}`}
                    expandIcon={<ExpandMoreIcon />}
                    IconButtonProps={{ disableRipple: true, disableFocusRipple: true }}
                    aria-controls='bin-report-list-item-expansion-panel-summary-content'
                    id='bin-report-list-item-expansion-panel-summary'
                    onMouseEnter={isReportWriteAllowed ? (): void => handleMouseEnteredReportListItem(id) : undefined}
                    onMouseLeave={isReportWriteAllowed ? (): void => handleMouseLeftReportListItem(id) : undefined}
                >
                    {name}

                    <ContextMenu
                        menuItems={[
                            {
                                itemName: language.get(LanguageMyReportList.ReportlistDeletereportsinfolder),
                                isEnabled: true, // Bin is Not Empty
                                icon: <Icon iconName={IconName.DELETE_FOREVER} />,
                                onClick: (): void => {
                                    handleDeleteItemsInFolder(reportListItem)
                                    onFolderActionsMouseOut()
                                },
                                onMouseOver: onFolderActionsMouseOver,
                                onMouseOut: onFolderActionsMouseOut
                            },
                            {
                                itemName: language.get(LanguageMyReportList.ReportlistRestorereportsinfolder),
                                isEnabled: true, // Bin is Not Empty
                                icon: <Icon iconName={IconName.RESTORE_FROM_TRASH} />,
                                onClick: (): void => {
                                    handleRestoreItemsInFolder(reportListItem)
                                    onFolderActionsMouseOut()
                                },
                                onMouseOver: onFolderActionsMouseOver,
                                onMouseOut: onFolderActionsMouseOut
                            }
                        ]}
                        position={ContextMenuPosition.TOP_RIGHT}
                        anchorElement={
                            <div className='edit-icon-wrapper'>
                                <IconButton
                                    type={IconButtonType.BUTTON}
                                    iconName={IconName.MORE_HORIZONTAL}
                                    title={language.get(LanguageMyReportList.ReportlistMoreoptions)}
                                />
                            </div>
                        }
                    />
                </AccordionSummary>
                <AccordionDetails className='accordion-details'>
                    {(isChecked || isShowContentUntilTransitionFinishes) && (
                        <div className={`report-list-wrapper`}>
                            {subItems.map((rptListItem) => (
                                <BinnedReportListItem
                                    key={rptListItem.id}
                                    reportListItem={rptListItem}
                                    propagatedValues={propagatedValues}
                                    isParentFolderHighlighted={isParentFolderHighlighted || isFolderHighlighted}
                                />
                            ))}
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>
        </div>
    ) : (
        <div
            onMouseEnter={isReportWriteAllowed ? (): void => handleMouseEnteredReportListItem(id) : undefined}
            onMouseLeave={isReportWriteAllowed ? (): void => handleMouseLeftReportListItem(id) : undefined}
            className={`report-list-file ${isChecked ? 'rlf-selected' : ''} ${
                isParentFolderHighlighted || isHighlighted ? 'highlight-row' : ''
            }`}
        >
            {isReportWriteAllowed && (
                <Checkbox
                    hideByDefault={true}
                    parentHover={isHovered}
                    onChange={(checked): void => handleReportListCheckboxClick(checked, id)}
                    isChecked={isChecked}
                />
            )}
            <div
                // onClick={(): void => handleReportListReportClick(reportListItem)}
                className='report-name'
                title={durationFilterSummary}
            >
                <div className='schedule-name-wrapper'>
                    {name}
                    {isScheduleActive && (
                        <div className='schedule'>
                            <Icon iconName={IconName.EMAIL} />
                        </div>
                    )}
                </div>
            </div>
            {isReportWriteAllowed && isHovered && (
                <ContextMenu
                    menuItems={[
                        {
                            itemName: language.get(LanguageMyReportList.ReportlistDeletereport),
                            isEnabled: isReportWriteAllowed,
                            icon: <Icon iconName={IconName.DELETE_FOREVER} />,
                            onClick: (): void => {
                                displayPromptOnDelete(id)
                                onMouseOut()
                            },
                            onMouseOver,
                            onMouseOut
                        },
                        {
                            itemName: language.get(LanguageMyReportList.ReportlistRestorereport),
                            isEnabled: isReportWriteAllowed,
                            icon: <Icon iconName={IconName.RESTORE_FROM_TRASH} />,
                            onClick: (): void => {
                                handleReportRestore(id)
                                onMouseOut()
                            },
                            onMouseOver,
                            onMouseOut
                        }
                    ]}
                    position={ContextMenuPosition.TOP_RIGHT}
                    anchorElement={
                        <div className='edit-icon-wrapper'>
                            <IconButton
                                type={IconButtonType.BUTTON}
                                iconName={IconName.MORE_HORIZONTAL}
                                title={language.get(LanguageMyReportList.ReportlistMoreoptions)}
                            />
                        </div>
                    }
                />
            )}
        </div>
    )
}

export default React.memo(BinnedReportListItem)
